import React, { useEffect, useState, useCallback, useRef } from "react";
import './MarketingMain.css'
import TeamHeader from "../TeamHeader/TeamHeader";
import { useLocation } from "react-router-dom";
import LoadingSpinner from '../../backend/components/LoadingSpinner';
import FilterDropdown from "../TeamPortalUtil/FilterDropdown";

// Auth
import { useAuth } from '../../Auth';

// Chart Imports
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

// States Data
import usState from '../../pages/DetectorFolder/util/us-states'
import brazil from '../../pages/DetectorFolder/util/br'
import australia from '../../pages/DetectorFolder/util/au'
import croatia from '../../pages/DetectorFolder/util/hr'

// Leaflet imports
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  ScaleControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import { Icon, Map as LeafletMap, LeafletEvent } from "leaflet";

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Icons
import { BiPieChartAlt2 } from "react-icons/bi";
import { BsList } from "react-icons/bs";
import { MdAssignmentInd } from "react-icons/md";
import { FaFlagCheckered } from "react-icons/fa";
import { RiNotionFill } from "react-icons/ri";

// Assets
import fnnLogo from '../../img/FNNLogoWithBlackFont-06.png'

// --- Interfaces ---

interface OrgUser {
  id: number;
  user_id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
}

interface MarketingOrg {
  id: number;
  ORG_PLAN: string;
  ORG_INDUSTRY: string;
  ORG_LOCATIONS: string[];
  ORG_NAME: string;
  ORG_LOGO: string;
  users: OrgUser[];
}

interface DetectorsPerRegion {
  GROUP_NAME: string;
  GROUP_DETECTORS: string;
  GROUP_DETECTORS_GOAL: number;
}

interface DetectorObject {
  mac: string;
  online: boolean;
}

interface ContactUs {
  contact_id: number;
  contact_email: string;
  created_at: string;
  contact_first_name: string;
  contact_last_name: string;
  website_subject: string;
  website_message: string;
  website_contact_status: "Unseen" | "Pending" | "Complete";
  website_complete_date: string
  website_assigned_to: number | null; // FK id
  comments: ContactUsComments[];
}

interface ContactUsComments {
  contact_message: number;
  comment_id: number;
  comment: string;
  comment_date: string;
  comment_creator: number;
  comment_creator_name: string;
}

interface ConferencesAttendance {
  id: string;
  name: string;
  color: string;
  attending: number;
}

interface Youtube {
  subscriber_count: number;
  view_count: number;
  video_count: number;
}

interface Twitter {
  id: number;
  twitter_followers: number;
  twitter_impressions: number;
}

export interface MarketingAsset {
  id: string;
  name: string;
  date: string;
  industry: string;
  industry_color: string;
  status: string;
  status_color: string;
}


const MarketingMain = () => {

  const { username, first_name, last_name } = useAuth();

  function displayNotification(successOrFail: boolean, message: string) {
    if (successOrFail) {
      toast.success(message, {
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#e7ecf1ff',
          color: 'black',
          fontSize: '16px',
        }
      });
    } else {
      toast.error(message, {
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#e7ecf1ff',
          color: 'black',
          fontSize: '16px',
        }
      });
    }
  }

  const location = useLocation();
  const preselectedRegion = location.state?.preselectedRegion || "All";
  const [selectedRegions, setSelectedRegions] = useState<string[]>([preselectedRegion]);

  const [orgs, setOrgs] = useState<MarketingOrg[]>([]);
  const [loading, setLoading] = useState(true);

  const PLAN_TYPES = ["Starter", "Pro", "Enterprise"];
  const INDUSTRY_TYPES = ["Wildfire", "Utility", "Other"];

  const fetchCsrfToken = async () => {
    const response = await fetch('/api/csrf-token/', {
      method: 'GET',
      credentials: "same-origin", // include cookies in the request
    });

    if (response.ok) {
      const data = await response.json();
      return data.csrfToken; // Return the CSRF token
    } else {
      console.error('Failed to fetch CSRF token');
      return null;
    }
  };

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const response = await fetch("/portal/api/marketing_orgs/", {
          method: "GET",
          credentials: "same-origin",
        });

        if (response.ok) {
          const data = await response.json();
          const trimmed: MarketingOrg[] = data.map((org: any) => ({
            id: org.id,
            ORG_NAME: org.ORG_NAME,
            ORG_LOGO: org.ORG_LOGO,
            ORG_PLAN: org.ORG_PLAN,
            ORG_INDUSTRY: org.ORG_INDUSTRY,
            ORG_LOCATIONS: org.ORG_LOCATIONS,
            users: org.users,
          }));
          setOrgs(trimmed);
        } else {
          console.error("Failed to fetch organizations", response.status);
        }
      } catch (error) {
        console.error("Error fetching organizations", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrgs();
  }, []);

  const [detectorDataMonitoring, setDetectorDataMonitoring] = useState<DetectorObject[]>([]);
  const detectorDataRef = useRef<DetectorObject[]>([]);

  useEffect(() => {
    const fetchMonitoring = async () => {
      try {
        const response = await fetch("/apidet/detector/monitoring", {
          credentials: "same-origin"
        });
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
        const data = await response.json();

        let alljson: any = {};
        if (data.dataUSA && !data.dataUSA.error) {
          const cleanedString = data.dataUSA.replace(/\\n/g, "").trim();
          alljson = JSON.parse(cleanedString);
        }

        const detectors: DetectorObject[] = Object.values(alljson)
          .filter(
            (d): d is DetectorObject =>
              typeof d === "object" &&
              d !== null &&
              "mac" in d &&
              "online" in d
          );

        setDetectorDataMonitoring(detectors);
        detectorDataRef.current = detectors;

      } catch (err) {
        console.error("Error fetching monitoring:", err);
        setDetectorDataMonitoring([]);
        detectorDataRef.current = [];
      }
    };

    fetchMonitoring();
    const interval = setInterval(fetchMonitoring, 5000);
    return () => clearInterval(interval);
  }, []);

  const [detectorsPerRegion, setdetectorsPerRegion] = useState<DetectorsPerRegion[]>([]);

  const fetchDetRegions = async () => {
    try {
      const res = await fetch("/api/detectordashapi/", {
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`Pipeline HTTP ${res.status}`);
      setdetectorsPerRegion(await res.json());
    } catch (err) {
      console.error("Error fetching Prefect Pipeline History:", err);
      setdetectorsPerRegion([]);
    }
  };

  useEffect(() => {
    fetchDetRegions();
  }, []);

  const formatRegionName = (name: string) =>
    name.replace(/^detectors_/, "")
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const normalize = (str: string) => {
    let cleaned = str.replace(/^detectors_/, "");
    const parts = cleaned.split("_");
    if (parts.length > 1) {
      const usStates = ["california", "texas", "florida", "newyork", "arizona", "colorado", "oregon", "washington", "nevada", "utah"];
      const firstPart = parts[0].toLowerCase().replace(/_/g, "");
      if (usStates.includes(firstPart)) {
        cleaned = parts[0];
      }
    }
    return cleaned.replace(/[_\s]/g, "").toLowerCase(); // ← also strip spaces
  };

  const getPlanCounts = (region: string) => {
    const counts: Record<string, number> = {};
    orgs.forEach((org) => {
      if (
        region === "All" ||
        org.ORG_LOCATIONS.some(
          (loc) => normalize(loc) === normalize(region)
        )
      ) {
        counts[org.ORG_PLAN] = (counts[org.ORG_PLAN] || 0) + 1;
      }
    });
    PLAN_TYPES.forEach((plan) => {
      if (!(plan in counts)) {
        counts[plan] = 0;
      }
    });
    return counts;
  };

  const getIndustryCounts = (region: string) => {
    const counts: Record<string, number> = {};
    orgs.forEach((org) => {
      if (
        region === "All" ||
        org.ORG_LOCATIONS.some(
          (loc) => normalize(loc) === normalize(region)
        )
      ) {
        counts[org.ORG_INDUSTRY] = (counts[org.ORG_INDUSTRY] || 0) + 1;
      }
    });
    INDUSTRY_TYPES.forEach((industry) => {
      if (!(industry in counts)) {
        counts[industry] = 0;
      }
    });
    return counts;
  };

  const getDetectorStatsForRegion = (regionName: string) => {
    const normalizedRegion = normalize(regionName);
    const monitoringData = detectorDataRef.current;

    const regionGroups = detectorsPerRegion.filter((group) => {
      const groupNormalized = normalize(group.GROUP_NAME);
      return groupNormalized === normalizedRegion ||
        groupNormalized.startsWith(normalizedRegion + "_") ||
        groupNormalized.startsWith(normalizedRegion);
    });

    if (regionGroups.length === 0) {
      return { total: 0, online: 0, offline: 0 };
    }

    const allMacs: string[] = [];
    regionGroups.forEach((group) => {
      if (group.GROUP_DETECTORS) {
        const macs = group.GROUP_DETECTORS
          .split(",")
          .map(m => m.trim())
          .filter(Boolean);
        allMacs.push(...macs);
      }
    });

    const statusMap = new Map<string, boolean>();
    monitoringData.forEach(d => {
      statusMap.set(d.mac, d.online);
    });

    let online = 0;
    let offline = 0;

    allMacs.forEach(mac => {
      const isOnline = statusMap.get(mac);
      if (isOnline === true) {
        online++;
      } else {
        offline++;
      }
    });

    return {
      total: allMacs.length,
      online,
      offline,
    };
  };

  const geojsonRef = useRef<L.GeoJSON | null>(null);

  const allRegionsData = {
    type: "FeatureCollection",
    features: [
      ...usState.features,
      ...brazil.features,
      ...australia.features,
      ...croatia.features,
    ]
  };

  const INDUSTRY_COLORS: Record<string, string> = {
    Utility: "#3F6FD8",
    Wildfire: "#FF6600",
    Other: "#8A94A6",
  };

  const PLAN_COLORS: Record<string, string> = {
    Starter: "#28aee1",
    Pro: "#ff9c00",
    Enterprise: "#FFD700",
  };

  const getOrgsInRegion = (regionName: string): MarketingOrg[] => {
    return orgs.filter((org) =>
      org.ORG_LOCATIONS.some(
        (loc) => normalize(loc) === normalize(regionName)
      )
    );
  };

  function getDetectorGoalColor(total: number, goal: number) {
    if (goal <= 0) return "rgb(255,204,0)"; // brighter yellow if no goal

    // Ratio capped at 1
    const ratio = Math.min(total / goal, 1);

    // Interpolate between bright yellow (255,204,0) and dark green (0,100,0)
    const r = Math.round(255 * (1 - ratio) + 0 * ratio);
    const g = Math.round(204 * (1 - ratio) + 100 * ratio);
    const b = 0;

    return `rgb(${r},${g},${b})`;
  }

  function style(feature: any) {
    const regionName = feature.properties?.name;
    const regionOrgs = regionName ? getOrgsInRegion(regionName) : [];
    const hasOrgs = regionOrgs.length > 0;

    if (!hasOrgs) {
      return {
        fillColor: "transparent",
        weight: 1,
        opacity: 0.5,
        color: "#ccc",
        fillOpacity: 0.3,
      };
    }

    // Sum detectors in region
    const regionGroups = detectorsPerRegion.filter((group) => {
      const normalized = normalize(group.GROUP_NAME);
      const regionNormalized = normalize(regionName);
      return normalized === regionNormalized ||
        normalized.startsWith(regionNormalized + "_") ||
        normalized.startsWith(regionNormalized);
    });

    let totalDetectors = 0;
    let goalDetectors = 0;

    regionGroups.forEach(group => {
      const macs = group.GROUP_DETECTORS
        ? group.GROUP_DETECTORS.split(",").filter(Boolean)
        : [];
      totalDetectors += macs.length;
      goalDetectors += group.GROUP_DETECTORS_GOAL || 0;
    });

    const fillColor = getDetectorGoalColor(totalDetectors, goalDetectors);

    return {
      fillColor,
      weight: 2,
      opacity: 1,
      color: "white",
      dashArray: "3",
      fillOpacity: 0.7,
    };
  }


  const computedStyle = useCallback(
    (feature: any) => style(feature),
    [detectorsPerRegion, orgs] // dependencies: re-run if these change
  );


  function highlightFeature(e: LeafletEvent) {
    const layer = e.target as L.Path;
    layer.setStyle({
      weight: 3,
      color: "#666",
      dashArray: "",
      fillOpacity: 0.7,
    });
    layer.bringToFront();
  }

  function onEachFeature(feature: GeoJSON.Feature, layer: any) {
    const regionName = feature.properties?.name;
    if (!regionName) return;

    const regionOrgs = getOrgsInRegion(regionName);
    if (regionOrgs.length === 0) {
      return;
    }

    layer.on({
      mouseover: (e: LeafletEvent) => {
        highlightFeature(e);
      },
      mouseout: (e: LeafletEvent) => {
        resetHighlight(e);
      },
      click: () => {
        const regionOrgs = getOrgsInRegion(regionName);
        const detectorStats = getDetectorStatsForRegion(regionName);

        let popupHtml = `
          <div class="map-popup">
            <div class="map-popup-header">
              ${formatRegionName(regionName)}
            </div>
            
            <div class="map-popup-detector-info">
              <div class="detector-info-title">Detector Information</div>
              <div class="detector-stats">
                <span>Total: <strong class="totaldet">${detectorStats.total}</strong> |</span>
                <span>Online: <strong class="ondet">${detectorStats.online}</strong> |</span>
                <span>Offline: <strong class="offdet">${detectorStats.offline}</strong></span>
              </div>
            </div>

            <div class="detector-info-title detector-info-customer-title">Customers</div>
        `;

        if (regionOrgs.length === 0) {
          popupHtml += `<div class="map-popup-empty">No organizations</div>`;
        }

        regionOrgs.forEach((org) => {
          popupHtml += `
    <div 
      class="map-popup-org"
      data-org-id="${org.id}"
      style="cursor:pointer;"
    >
      <img
        src="${org.ORG_LOGO ? org.ORG_LOGO : fnnLogo}"
        alt="${org.ORG_NAME}"
        class="map-popup-logo"
        onerror="this.style.display='none'"
      />
      <div class="map-popup-org-info">
        <div class="map-popup-org-name">${org.ORG_NAME}</div>
        <div class="map-popup-org-meta">
          <span class="industry ${org.ORG_INDUSTRY.toLowerCase()}">
            ${org.ORG_INDUSTRY}
          </span>
          <span class="plan ${org.ORG_PLAN.toLowerCase()}">
            ${org.ORG_PLAN}
          </span>
        </div>
      </div>
    </div>
  `;
        });


        popupHtml += `</div>`;

        layer.bindPopup(popupHtml, {
          className: 'marketing-region-popup'
        }).openPopup();

        setTimeout(() => {
          const elements = document.querySelectorAll(".map-popup-org");

          elements.forEach((el) => {
            el.addEventListener("click", () => {
              const orgId = el.getAttribute("data-org-id");

              if (orgId) {
                window.open(
                  `/marketing/customers?org=${orgId}`,
                  "_blank"
                );
              }
            });
          });
        }, 0);
      }
    });
  }

  function resetHighlight(e: LeafletEvent) {
    if (geojsonRef.current) {
      geojsonRef.current.resetStyle(e.target);
    }
  }

  /* ----------------------------------------------------
    Plan and Industry Charts
  ---------------------------------------------------- */
  const industryCounts = getIndustryCounts(selectedRegions[0]);
  const planCounts = getPlanCounts(selectedRegions[0]);
  const [viewMode, setViewMode] = useState<'chart' | 'list'>('chart');

  const industryTotal = Object.values(industryCounts).reduce((a, b) => a + b, 0);
  const planTotal = Object.values(planCounts).reduce((a, b) => a + b, 0);

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1600);
  const donutTotalFontSize = isSmallScreen ? '12px' : '20px';
  const valueSpacing = isSmallScreen ? -20 : -25;
  const nameSpacing = isSmallScreen ? 18 : 20;
  const legendFontSize = isSmallScreen ? '12px' : '14px';
  const legendMarkerSize = isSmallScreen ? 5 : 7;

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 1600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const industryChartOptions: ApexOptions = {
    chart: { type: 'donut' },
    labels: INDUSTRY_TYPES,
    colors: INDUSTRY_TYPES.map(industry => INDUSTRY_COLORS[industry]),
    legend: {
      position: 'bottom',
      fontSize: legendFontSize,
      horizontalAlign: 'center',
      offsetY: 0,
      markers: {
        size: legendMarkerSize,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '80%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: donutTotalFontSize,
              fontWeight: 600,
              color: '#373d3f',
              formatter: () => industryTotal.toString(),
            },

            name: {
              show: true,
              offsetY: nameSpacing,  // move name (slice label) up to top
            },

            value: {
              show: true,
              fontSize: donutTotalFontSize,
              color: '#373d3f',
              offsetY: valueSpacing,
            },
          },
        },
      },
    },

    dataLabels: { enabled: false },
    tooltip: {
      y: {
        formatter: (val) => {
          const percent = ((val / industryTotal) * 100).toFixed(1);
          return `${val} (${percent}%)`;
        },
      },
    },
  };

  const industryChartSeries = INDUSTRY_TYPES.map(industry => industryCounts[industry]);

  const planChartOptions: ApexOptions = {
    chart: { type: 'donut' },
    labels: PLAN_TYPES,
    colors: PLAN_TYPES.map(plan => PLAN_COLORS[plan]),
    legend: {
      position: 'bottom',
      fontSize: legendFontSize,
      horizontalAlign: 'center',
      offsetY: 0,
      markers: {
        size: legendMarkerSize,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '80%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: donutTotalFontSize,
              fontWeight: 600,
              color: '#373d3f',
              formatter: () => planTotal.toString(),
            },

            name: {
              show: true,
              offsetY: nameSpacing,  // move name (slice label) up to top
            },

            value: {
              show: true,
              fontSize: donutTotalFontSize,
              color: '#373d3f',
              offsetY: valueSpacing,
            },
          },
        },
      },
    },
    dataLabels: { enabled: false },
    tooltip: {
      y: {
        formatter: (val) => {
          const percent = ((val / planTotal) * 100).toFixed(1);
          return `${val} (${percent}%)`;
        },
      },
    },
  };

  const planChartSeries = PLAN_TYPES.map(plan => planCounts[plan]);

  // Mailjet contact section
  const [contacts, setContacts] = useState<ContactUs[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  const [assigneeSearch, setAssigneeSearch] = useState<Record<number, string>>({});
  const [assigneeFocused, setAssigneeFocused] = useState<number | null>(null);

  // States to handle contact assignment
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactUs | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState<OrgUser | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const contactsRef = useRef<HTMLDivElement | null>(null);
  const [committedAssigneeSearch, setCommittedAssigneeSearch] = useState<Record<number, string>>({});

  // States to handle contact completion
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [contactToComplete, setContactToComplete] = useState<ContactUs | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  // Conference popup:
  const [showConferencePopup, setShowConferencePopup] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoadingContacts(true);
      const res = await fetch("/api/contact_fnn/list/", {
        credentials: "same-origin",
      });

      if (!res.ok) throw new Error("Failed to fetch contacts");

      const data: ContactUs[] = await res.json();
      setContacts(data);
    } catch (err) {
      setContactsError(err instanceof Error ? err.message : "Error loading contacts");
    } finally {
      setLoadingContacts(false);
    }
  };

  const toggleRow = (id: number) => {
    setExpandedRowId(prev => (prev === id ? null : id));
  };

  const formatDate = (dateString?: string, timeOnSecondLine = true) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    // Format date in UTC
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });

    // Format time in UTC with AM/PM
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // AM/PM format
      timeZone: "UTC",
    });

    if (timeOnSecondLine) {
      return (
        <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2" }}>
          <span>{formattedDate}</span>
          <span>{formattedTime}</span>
        </div>
      );
    } else {
      return (
        <span>
          {formattedDate}, {formattedTime}
        </span>
      );
    }
  };


  // Fetching FNN User only
  const [fnnUsers, setFnnUsers] = useState<OrgUser[]>([]);
  const [newComments, setNewComments] = useState<Record<number, string>>({});


  const fetchFNNUsers = async () => {
    try {
      const response = await fetch('/api/inventory/fnn-users/', {
        credentials: "same-origin",
      });
      const data: OrgUser[] = await response.json();

      if (response.ok) {
        setFnnUsers(data);
      } else {
        console.error("Failed to load FNN users:", (data as any).error || response.statusText);
      }
    } catch (error) {
      console.error("❌ Error fetching FNN users:", error);
    }
  };

  useEffect(() => {
    fetchFNNUsers();
  }, []);



  const findUserByNameOrEmail = (input: string): OrgUser | null => {
    const normalized = input.toLowerCase().trim();
    return fnnUsers.find(user =>
      `${user.first_name} ${user.last_name}`.toLowerCase() === normalized ||
      user.email.toLowerCase() === normalized
    ) || null;
  };

  const getUserById = (id: number | null): OrgUser | null => {
    if (!id) return null;
    return fnnUsers.find(user => user.id === id) || null;
  };

  const assignContact = async () => {
    if (!selectedContact || !selectedAssignee) return;

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) return;

    try {
      setIsAssigning(true);

      const res = await fetch(
        "/api/contact_fnn/update-status/",
        {
          method: "PUT",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({
            contact_id: selectedContact.contact_id,
            status: "Pending",
            assigned_user_id: selectedAssignee.user_id,
          }),
        }
      );

      if (!res.ok) throw new Error("Assignment failed");

      await fetchContacts();
      displayNotification(true, "Contact assigned successfully!");
      setShowAssignModal(false);
    } catch {
      displayNotification(false, "Failed to assign contact");
    } finally {
      setIsAssigning(false);
    }
  };

  const markContactComplete = async () => {
    if (!contactToComplete) return;

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) return;

    try {
      setIsCompleting(true);

      const res = await fetch(
        "/api/contact_fnn/update-status/",
        {
          method: "PUT",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({
            contact_id: contactToComplete.contact_id,
            status: "Complete",
          }),
        }
      );

      if (!res.ok) throw new Error();

      await fetchContacts();
      displayNotification(true, "Contact marked as completed");
      setShowCompleteModal(false);
    } catch {
      displayNotification(false, "Failed to complete contact");
    } finally {
      setIsCompleting(false);
    }
  };


  // Util function to find the id of the logged in user
  const loggedFnnUserId = React.useMemo(() => {
    if (!fnnUsers?.length) return null;

    const matchedUser = fnnUsers.find(
      (user) =>
        user.username === username &&
        user.first_name === first_name &&
        user.last_name === last_name
    );

    return matchedUser ? matchedUser.id : null;
  }, [fnnUsers, username, first_name, last_name]);

  const addComment = async (
    contact: ContactUs,
    commentText: string
  ) => {
    if (!loggedFnnUserId) return;

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) return;

    try {
      const res = await fetch(
        "/api/contact_fnn/comment-post/",
        {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({
            contact_message: contact.contact_id, // ✅ always correct
            comment: commentText,
            comment_creator: loggedFnnUserId,
          }),
        }
      );

      if (!res.ok) throw new Error("Posting comment failed");

      await fetchContacts();
      displayNotification(true, "Comment added successfully!");
    } catch {
      displayNotification(false, "Failed to add comment");
    }
  };

  const handlePostComment = async (contact: ContactUs) => {
    const commentText = newComments[contact.contact_id]?.trim();
    if (!commentText) return;

    await addComment(contact, commentText);

    setNewComments(prev => ({
      ...prev,
      [contact.contact_id]: "",
    }));
  };

  // Closing contact if clicking anywhere that is not it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        contactsRef.current &&
        !contactsRef.current.contains(event.target as Node)
      ) {
        // 🚀 Do NOT close if modal is opening
        if (showAssignModal || showCompleteModal) return;

        setSelectedContact(null);
        setExpandedRowId(null);
        setAssigneeFocused(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAssignModal, showCompleteModal]);


  /* ----------------------------------------------------
    Conferences section - Notion
  ---------------------------------------------------- */
  const [conferencesData, setConferencesData] = useState<ConferencesAttendance[]>([]);
  const [conferencesLoading, setConferencesLoading] = useState(true);
  const [conferences, setConferences] = useState<MarketingAsset[]>([]);
  const [loadingConferences, setLoadingConferences] = useState(true);


  const fetchConferencesData = async () => {
    try {
      const response = await fetch(
        "/api/notion/marketing/industry-attendance/",
        {
          method: "GET",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data: ConferencesAttendance[] = await response.json();
      setConferencesData(data);
    } catch (error) {
      console.error("Failed to fetch industry attendance:", error);
    } finally {
      setConferencesLoading(false);
    }
  };

  const fetchConferences = async () => {
    try {
      setLoadingConferences(true);

      const response = await fetch(
        "/api/notion/block-children/2fc3634f-c8d9-8013-b2cc-c813079058ed/",
        { credentials: "same-origin" }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error("Failed to load conferences:", data?.error || response.statusText);
        return;
      }

      const dbBlock = data.blocks?.find(
        (block: any) => block.type === "child_database"
      );

      if (!dbBlock?.rows) return;

      const formatted: MarketingAsset[] = dbBlock.rows.map((row: any) => {
        const props = row.properties;

        const start = props.Date?.date?.start;
        const end = props.Date?.date?.end;

        const formattedDate =
          start && end
            ? `${new Date(start).toLocaleDateString()} - ${new Date(end).toLocaleDateString()}`
            : start
              ? new Date(start).toLocaleDateString()
              : "";

        return {
          id: row.id,
          name: props.Name?.title?.[0]?.plain_text || "",
          date: formattedDate,
          industry: props.Industry?.select?.name || "Other",
          industry_color: props.Industry?.select?.color || "gray",
          status: props.Status?.status?.name || "",
          status_color: props.Status?.status?.color || "default",
        };
      });

      setConferences(formatted);

    } catch (error) {
      console.error("❌ Error fetching conferences:", error);
    } finally {
      setLoadingConferences(false);
    }
  };


  useEffect(() => {
    fetchConferencesData();
    fetchConferences();
  }, []);

  const groupedConferences = conferences.reduce((acc, conf) => {
    if (!acc[conf.industry]) {
      acc[conf.industry] = [];
    }
    acc[conf.industry].push(conf);
    return acc;
  }, {} as Record<string, MarketingAsset[]>);

  const mapNotionColor = (color: string) => {
    const map: Record<string, string> = {
      purple: "#7C3AED",
      blue: "#2563EB",
      orange: "#EA580C",
      green: "#16A34A",
      red: "#DC2626",
      default: "#555555"
    };

    return map[color] || "#555555";
  };

  const mapNotionStatusColor = (color: string) => {
    const colors: Record<
      string,
      { background: string; }
    > = {
      green: {
        background: "#6ba273",
      },
      blue: {
        background: "#28aee1",
      },
      red: {
        background: "#d85546",
      },
      default: {
        background: "#7b5536",
      },
    };

    return colors[color] || colors.default;
  };

  /* ----------------------------------------------------
    Social Media Section
  ---------------------------------------------------- */
  const [youtubeData, setYoutubeData] = useState<Youtube | null>(null);
  const [twitterData, setTwitterData] = useState<Twitter | null>(null);
  const [socialLoading, setSocialLoading] = useState(true);

  const fetchYoutubeData = async () => {
    try {
      const response = await fetch(
        "/portal/api/youtube/subscribers/",
        {
          method: "GET",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data: Youtube = await response.json();
      setYoutubeData(data);
    } catch (error) {
      console.error("Failed to fetch youtube data:", error);
    } finally {
      setSocialLoading(false);
    }
  };

  const fetchTwitterData = async () => {
    try {
      const response = await fetch(
        "/portal/api/twitter/subscribers",
        {
          method: "GET",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data: Twitter = await response.json();
      setTwitterData(data);
    } catch (error) {
      console.error("Failed to fetch twitter data:", error);
    } finally {
      setSocialLoading(false);
    }
  };

  useEffect(() => {
    fetchYoutubeData();
    fetchTwitterData();
  }, []);

  /* ----------------------------------------------------
   Conferences Chart Data
---------------------------------------------------- */
  const [marketingViewMode, setMarketingViewMode] =
    useState<'chart' | 'list'>('chart');


  const conferencesLabels = conferencesData.map(c => c.name);
  const conferencesSeries = conferencesData.map(c => c.attending);
  const conferencesColors = conferencesData.map(c => c.color);

  const conferencesTotal = conferencesSeries.reduce((a, b) => a + b, 0);

  const conferencesChartOptions: ApexOptions = {
    chart: { type: 'donut' },
    labels: conferencesLabels,
    colors: conferencesColors,
    legend: {
      position: 'bottom',
      fontSize: legendFontSize,
      horizontalAlign: 'center',
      offsetY: 0,
      markers: {
        size: legendMarkerSize,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '80%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: donutTotalFontSize,
              fontWeight: 600,
              color: '#373d3f',
              formatter: () => conferencesTotal.toString(),
            },

            name: {
              show: true,
              offsetY: nameSpacing,  // move name (slice label) up to top
            },

            value: {
              show: true,
              fontSize: donutTotalFontSize,
              color: '#373d3f',
              offsetY: valueSpacing,
            },
          },
        },
      },
    },

    tooltip: {
      custom: function ({ series, seriesIndex, w }) {
        const value = series[seriesIndex];
        const label = w.globals.labels[seriesIndex];

        return `
      <div style="
        padding: 8px 10px;
        background: ${conferencesColors};
        border-radius: 6px;
        font-size: 13px;
      ">
        ${label} attendance: ${value}
      </div>
    `;
      },
    },


    dataLabels: { enabled: false },
  };

  /* ----------------------------------------------------
     Social Media Chart Data
  ---------------------------------------------------- */

  const youtubeSubscribers = youtubeData?.subscriber_count ?? 0;
  const twitterFollowers = twitterData?.twitter_followers ?? 0;
  const socialSeries = [youtubeSubscribers, twitterFollowers];

  const youtubeViews = youtubeData?.view_count ?? 0;
  const twitterImpressions = twitterData?.twitter_impressions ?? 0;
  const socialImpressions = [youtubeViews, twitterImpressions];


  const socialChartOptions: ApexOptions = {
    chart: { type: 'donut' },
    labels: ['YouTube', 'X'],
    colors: ['#FF0000', '#000000'],
    legend: {
      position: 'bottom',
      fontSize: legendFontSize,
      horizontalAlign: 'center',
      offsetY: 0,
      markers: {
        size: legendMarkerSize,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '80%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: donutTotalFontSize,
              fontWeight: 600,
              color: '#373d3f',
              formatter: () => (youtubeSubscribers + twitterFollowers).toString(),
            },

            name: {
              show: true,
              offsetY: nameSpacing,  // move name (slice label) up to top
            },

            value: {
              show: true,
              fontSize: donutTotalFontSize,
              color: '#373d3f',
              offsetY: valueSpacing,
            },
          },
        },
      },
    },
    dataLabels: { enabled: false },

    /* 🔥 CUSTOM TOOLTIP */
    tooltip: {
      custom: function ({ seriesIndex }) {
        const followers = socialSeries[seriesIndex];
        const impressions = socialImpressions[seriesIndex];
        const label = ['YouTube', 'X'][seriesIndex];

        return `
        <div style="
          padding: 10px;
          font-size: 13px;
        ">
          <strong>${label}</strong><br/>
          Followers: ${followers.toLocaleString()}<br/>
          Impressions: ${impressions.toLocaleString()}
        </div>
      `;
      },
    },
  };


  /* ----------------------------
   Conferences List Data
-----------------------------*/
  const conferenceTotal = conferencesSeries.reduce((a, b) => a + b, 0);

  /* ----------------------------
     Social Media List Data
  -----------------------------*/
  const socialTotal = youtubeSubscribers + twitterFollowers;

  // Filter
  const availableStatuses = ["Unseen", "Pending", "Complete"];
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const availableSubjects = ["General", "Detector", "Employment"];
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);

  const filteredContacts = contacts.filter(contact => {
    // Status filter
    const statusMatch =
      selectedStatuses.length === 0 ||
      selectedStatuses.includes(contact.website_contact_status);

    // Subject filter
    const subjectMatch =
      selectedSubjects.length === 0 ||
      selectedSubjects.some(subject =>
        contact.website_subject.startsWith(subject)
      );

    return statusMatch && subjectMatch;
  });


  return (
    <div className="customer-directory-body toa-body">
      <TeamHeader title="Marketing" />
      <ToastContainer />

      <div className="customer-title-and-expand-buttons">
        <h1 className="cutomer-title">Customers & Analytics</h1>
      </div>

      <div className='toa-dash-container'>

        <div className="toa-dash-block">
          {loading ? (
            <div className="toa-map-skeleton">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="toa-map-wrapper">
              <MapContainer
                center={[0, 0]}
                zoom={0}
                minZoom={2}
                style={{ width: "100%", height: "100%" }}
              >
                <TileLayer
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <ScaleControl
                  position="bottomleft"
                  metric={true}
                  imperial={false}
                  maxWidth={200}
                />
                <GeoJSON
                  data={allRegionsData as GeoJSON.GeoJsonObject}
                  style={computedStyle}
                  onEachFeature={onEachFeature}
                  ref={geojsonRef}
                />
              </MapContainer>
            </div>
          )}
        </div>


        {/* Mailjet Contact Table */}
        <div className="toa-dash-block" ref={contactsRef}>
          <div className="request-title-and-times toa-location">
            <h1 className='toa-dash-block-performance-title'>Contact Us Requests</h1>

            <FilterDropdown
              dropdownClassName="marketing-contact-dropdown"
              sections={[
                {
                  key: "status",
                  title: "Status",
                  options: availableStatuses
                },
                {
                  key: "subject",
                  title: "Subject",
                  options: availableSubjects
                }
              ]}
              onChange={(filters) => {
                setSelectedStatuses(filters.status || []);
                setSelectedSubjects(filters.subject || []);
              }}
            />

          </div>

          {loadingContacts ? (
            <div className="mailjet-contact-table">
              <p>Loading contacts...</p>
            </div>
          ) : contactsError ? (
            <div className="mailjet-contact-table">
              <p className="error">Error: {contactsError}</p>
            </div>
          ) : (
            <div className="mailjet-contact-table">
              <div className="mailjet-contact-table-header">
                <div>Name</div>
                <div>Subject</div>
                <div>Date (UTC)</div>
                <div>Status</div>
              </div>

              <div className="mailjet-contact-table-body">
                {filteredContacts.map((contact) => (
                  <React.Fragment key={contact.contact_id}>
                    <div
                      className={`mailjet-contact-table-row ${expandedRowId === contact.contact_id ? "active" : ""}`}
                      onClick={() => toggleRow(contact.contact_id)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="mailjet-contact-code">
                        {contact.contact_first_name} {contact.contact_last_name}
                      </div>
                      <div className="mailjet-contact-code">{contact.website_subject}</div>
                      <div className="mailjet-contact-code">{formatDate(contact.created_at)}</div>
                      <div
                        className="mailjet-contact-code"
                        style={{
                          fontWeight: 600,
                          color:
                            contact.website_contact_status === "Complete"
                              ? "#20bf55"
                              : contact.website_contact_status === "Pending"
                                ? "#f9a825"
                                : contact.website_contact_status === "Unseen"
                                  ? "#e53935"
                                  : "inherit", // fallback color
                        }}
                      >
                        {contact.website_contact_status}
                      </div>

                    </div>

                    {expandedRowId === contact.contact_id && (
                      <div className="mailjet-contact-expanded-row" onClick={(e) => e.stopPropagation()}>
                        <div className="mailjet-contact-details">

                          <div className="mailjet-contact-detail-item-group">
                            <div className="mailjet-contact-detail-item">
                              <strong>Email</strong> {contact.contact_email}
                            </div>

                            <div className="mailjet-contact-detail-item" style={{ position: "relative", gap: "0.5rem" }}>
                              <strong>Assigned To</strong>

                              {contact.website_contact_status === "Complete" ? (
                                <div style={{ marginTop: '0.5rem' }}>
                                  {contact.website_assigned_to
                                    ? `${getUserById(contact.website_assigned_to)?.first_name} ${getUserById(contact.website_assigned_to)?.last_name}`
                                    : '—'
                                  }
                                </div>
                              ) : (
                                <>
                                  <input
                                    type="text"
                                    value={
                                      assigneeSearch[contact.contact_id] ??
                                      (contact.website_assigned_to
                                        ? `${getUserById(contact.website_assigned_to)?.first_name} ${getUserById(contact.website_assigned_to)?.last_name}`
                                        : ""
                                      )
                                    }
                                    placeholder="Click to assign or type name/email"
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={() => setAssigneeFocused(contact.contact_id)}
                                    onChange={(e) => {
                                      setAssigneeSearch(prev => ({
                                        ...prev,
                                        [contact.contact_id]: e.target.value,
                                      }));
                                      setAssigneeFocused(contact.contact_id);
                                    }}
                                    onBlur={() => {
                                      // Delay to allow click on dropdown items
                                      setTimeout(() => setAssigneeFocused(null), 200);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        const user = findUserByNameOrEmail(assigneeSearch[contact.contact_id] || "");

                                        if (!user) {
                                          displayNotification(false, "Invalid contact");
                                          return;
                                        }

                                        setSelectedContact(contact);
                                        setSelectedAssignee(user);
                                        setShowAssignModal(true);
                                        setAssigneeFocused(null);
                                      } else if (e.key === "Escape") {
                                        setAssigneeFocused(null);
                                      }
                                    }}
                                  />

                                  {(assigneeFocused === contact.contact_id && fnnUsers.length > 0) && (
                                    <div className="autocomplete-list mailjet-autocomplete" onClick={(e) => e.stopPropagation()}>
                                      {fnnUsers
                                        .filter(user =>
                                          `${user.first_name} ${user.last_name}`.toLowerCase().includes((assigneeSearch[contact.contact_id] || "").toLowerCase())
                                        )
                                        .map(user => (
                                          <div
                                            key={user.user_id}
                                            className="autocomplete-item"
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              const fullName = `${user.first_name} ${user.last_name}`;
                                              setAssigneeSearch(prev => ({
                                                ...prev,
                                                [contact.contact_id]: fullName,   // show in input while modal is open
                                              }));
                                              setSelectedContact(contact);
                                              setSelectedAssignee(user);
                                              setShowAssignModal(true);
                                              setAssigneeFocused(null);
                                            }}
                                          >
                                            {user.first_name} {user.last_name} ({user.email})
                                          </div>
                                        ))}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>

                            <div className="mailjet-contact-detail-item">
                              <strong>Completed At</strong> {formatDate(contact.website_complete_date)}
                            </div>

                            {contact.website_contact_status !== "Complete" && (
                              <div className="mailjet-contact-detail-item" style={{ gap: "0.5rem" }}>
                                <strong>Update Status</strong>
                                <button
                                  className="confirm-btn mailjet-confirm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setContactToComplete(contact);
                                    setShowCompleteModal(true);
                                  }}
                                >
                                  Mark as Complete
                                </button>
                              </div>
                            )}


                          </div>

                          <div className="mailjet-contact-detail-item">
                            <strong>Message:</strong>
                            <p>{contact.website_message}</p>
                          </div>

                          <div className="mailjet-contact-detail-item">

                            <div>
                              <strong>Internal Comments:</strong>

                              <div className="contact-create-comment-wrapper">
                                <input
                                  type="text"
                                  placeholder="Add a comment..."
                                  value={newComments[contact.contact_id] || ""}
                                  onChange={(e) =>
                                    setNewComments(prev => ({
                                      ...prev,
                                      [contact.contact_id]: e.target.value,
                                    }))
                                  }
                                  onKeyDown={async (e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      await handlePostComment(contact);
                                    }
                                  }}
                                />
                                <button
                                  className="confirm-btn mailjet-confirm"
                                  onClick={async () => handlePostComment(contact)}
                                >
                                  Post
                                </button>
                              </div>

                            </div>

                            {contact.comments.length === 0 ? (
                              <p style={{ fontStyle: "italic", color: "#666" }}>No comments yet</p>
                            ) : (
                              <ul className="contact-comments-list">
                                {contact.comments.map((comment) => (
                                  <li key={comment.comment_id} className="contact-comment-item">
                                    <div className="comment-header">
                                      <strong>{comment.comment_creator_name}</strong>{" "}
                                      <span style={{ fontSize: "0.85rem", color: "#999" }}>
                                        {formatDate(comment.comment_date, false)}
                                      </span>
                                    </div>
                                    <div>{comment.comment}</div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>

                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}

                {contacts.length === 0 && (
                  <div className="mailjet-contact-table-row">
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                      No contacts found
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Customer Overview Charts */}
        <div className="toa-dash-block marketing-chart-block">
          <div className="request-title-and-times toa-location">
            <h1 className='toa-dash-block-performance-title'>Customer Overview</h1>

            <div className="marketing-chart-table-toggle">
              <BiPieChartAlt2
                className={viewMode === 'chart' ? 'active' : ''}
                onClick={() => setViewMode('chart')}
              />
              <BsList
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
              />
            </div>
          </div>

          {viewMode === 'chart' ? (
            <div className='toa-charts'>
              <div className='toa-chart'>
                <h1 className='marketing-donut-title'>Industry</h1>

                <div className='marketing-donut'>
                  <ReactApexChart
                    options={industryChartOptions}
                    series={industryChartSeries}
                    type="donut"
                    height="100%"
                  />
                </div>
              </div>

              <div className='toa-chart'>
                <h1 className='marketing-donut-title'>Plans</h1>
                <div className='marketing-donut'>
                  <ReactApexChart
                    options={planChartOptions}
                    series={planChartSeries}
                    type="donut"
                    height="100%"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className='marketing-lists'>
              <div className='marketing-list'>
                <div className='marketing-resources-group-title-and-notion'>
                  <h1 className='marketing-list-title' style={{ marginBottom: 'unset', marginTop: '0.5rem' }}>
                    Industry
                  </h1>

                </div>
                <ul className='marketing-stats-list'>
                  {INDUSTRY_TYPES.map((industry) => (
                    <li key={industry}>
                      <span className='marketing-label' style={{ color: INDUSTRY_COLORS[industry] }}>
                        {industry}
                      </span>
                      <span className='marketing-value'>{industryCounts[industry]}</span>
                    </li>
                  ))}
                  <li className='marketing-total'>
                    <span className='marketing-label'>Total</span>
                    <span className='marketing-value'>{industryTotal}</span>
                  </li>
                </ul>
              </div>

              <div className='marketing-list'>
                <div className='marketing-resources-group-title-and-notion'>
                  <h1 className='marketing-list-title' style={{ marginBottom: 'unset', marginTop: '0.5rem' }}>
                    Plans
                  </h1>

                </div>
                <ul className='marketing-stats-list'>
                  {PLAN_TYPES.map((plan) => (
                    <li key={plan}>
                      <span className='marketing-label' style={{ color: PLAN_COLORS[plan] }}>
                        {plan}
                      </span>
                      <span className='marketing-value'>{planCounts[plan]}</span>
                    </li>
                  ))}
                  <li className='marketing-total'>
                    <span className='marketing-label'>Total</span>
                    <span className='marketing-value'>{planTotal}</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Social Media and Other Charts */}
        <div className="toa-dash-block marketing-chart-block">
          <div className="request-title-and-times toa-location">
            <h1 className='toa-dash-block-performance-title'>
              Marketing Stategy
            </h1>

            <div className="marketing-chart-table-toggle">
              <BiPieChartAlt2
                className={marketingViewMode === 'chart' ? 'active' : ''}
                onClick={() => setMarketingViewMode('chart')}
              />
              <BsList
                className={marketingViewMode === 'list' ? 'active' : ''}
                onClick={() => setMarketingViewMode('list')}
              />
            </div>
          </div>

          {(conferencesLoading || socialLoading) ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem', height: '100%' }}>
              <LoadingSpinner />
            </div>
          ) : marketingViewMode === 'chart' ? (

            /* ---------------- CHART VIEW ---------------- */

            <div className='toa-charts'>
              {/* Conferences Chart */}
              <div className='toa-chart'>

                <div className="marketing-list-entry-with-icon">

                  <div className='marketing-resources-group-title-and-notion' style={{ marginBottom: 'unset' }}>
                    <h1 className='marketing-donut-title'>Conferences 2026</h1>
                    <a href="https://www.notion.so/Marketing-Strategy-2026-2fc3634fc8d980589241eb13d3a40f0a#2fc3634fc8d980c7930ece32cb9cc438" target="_blank" rel="noreferrer">
                      <button style={{ marginBottom: '0.5rem' }}>
                        <RiNotionFill />
                      </button>
                    </a>
                  </div>

                  <button className="marketing-list-entry-button on-donut"
                    onClick={() => {
                      setShowConferencePopup(true);
                    }}>
                    Details
                  </button>

                </div>

                <div className='marketing-donut'>
                  <ReactApexChart
                    options={conferencesChartOptions}
                    series={conferencesSeries}
                    type="donut"
                    height="100%"
                  />
                </div>
              </div>

              {/* Social Media Chart */}
              <div className='toa-chart'>
                <h1 className='marketing-donut-title' style={{ marginBottom: 'unset', marginTop: '0.5rem' }}>Social Media</h1>
                <div className='marketing-donut'>
                  <ReactApexChart
                    options={socialChartOptions}
                    series={socialSeries}
                    type="donut"
                    height="100%"
                  />
                </div>
              </div>
            </div>

          ) : (

            /* ---------------- LIST VIEW ---------------- */

            <div className='marketing-lists'>

              {/* Conferences List */}
              <div className='marketing-list'>

                <div className="marketing-list-entry-with-icon">

                  <div className='marketing-resources-group-title-and-notion' style={{ marginBottom: 'unset' }}>
                    <h1 className='marketing-list-title'>
                      Conferences 2026
                    </h1>
                    <a href="https://www.notion.so/Marketing-Strategy-2026-2fc3634fc8d980589241eb13d3a40f0a#2fc3634fc8d980c7930ece32cb9cc438" target="_blank" rel="noreferrer">
                      <button style={{ marginBottom: '0.5rem' }}>
                        <RiNotionFill />
                      </button>
                    </a>
                  </div>

                  <button className="marketing-list-entry-button"
                    onClick={() => {
                      setShowConferencePopup(true);
                    }}>
                    Details
                  </button>
                </div>

                <ul className='marketing-stats-list'>
                  {conferencesData.map((conf) => (
                    <li key={conf.name}>
                      <span
                        className='marketing-label'
                        style={{ color: conf.color }}
                      >
                        {conf.name}
                      </span>
                      <span className='marketing-value'>
                        {conf.attending}
                      </span>
                    </li>
                  ))}

                  <li className='marketing-total'>
                    <span className='marketing-label'>Total</span>
                    <span className='marketing-value'>{conferenceTotal}</span>
                  </li>
                </ul>
              </div>

              {/* Social Media List */}
              <div className='marketing-list'>
                <div className='marketing-resources-group-title-and-notion'>
                  <h1 className='marketing-list-title' style={{ marginBottom: 'unset', marginTop: '0.5rem' }}>
                    Social Media
                  </h1>

                </div>

                <ul className='marketing-stats-list'>
                  <li>
                    <span
                      className='marketing-label'
                      style={{ color: '#FF0000' }}
                    >
                      YouTube
                    </span>
                    <span className='marketing-value'>
                      {youtubeSubscribers}
                    </span>
                  </li>

                  <li>
                    <span
                      className='marketing-label'
                      style={{ color: '#000000' }}
                    >
                      X
                    </span>
                    <span className='marketing-value'>
                      {twitterFollowers}
                    </span>
                  </li>

                  <li className='marketing-total'>
                    <span className='marketing-label'>Total</span>
                    <span className='marketing-value'>{socialTotal}</span>
                  </li>
                </ul>
              </div>

            </div>
          )}
        </div>



      </div>

      {/* Assign Contact Modal */}
      {showAssignModal && selectedContact && selectedAssignee && (
        <div className="add-item-popup-overlay">
          <div className="add-item-popup-content hh-del-pop">
            {isAssigning ? (
              <div
                style={{
                  height: "10rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LoadingSpinner />
              </div>
            ) : (
              <div className="hh-delete-inner-pop" style={{ gap: "2rem" }}>
                <div className="hh-delete-text-and-icon">
                  <MdAssignmentInd className="hardware2-delete-icon" />

                  <div>
                    <h3>Assign Contact</h3>

                    <p>
                      You are about to assign the responsibility to handle this contact
                      from <strong>
                        {selectedContact.contact_first_name} {selectedContact.contact_last_name}
                      </strong>
                    </p>

                    <p style={{ marginTop: "1rem" }}>
                      <strong>To:</strong><br />
                      {selectedAssignee.first_name} {selectedAssignee.last_name} ({selectedAssignee.email})
                    </p>

                    <p style={{ marginTop: "1rem", color: "#28aee1" }}>
                      The assignee will be notified via email.
                    </p>
                  </div>
                </div>

                <div className="hh-duo hh-actions">
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      // Revert the input to whatever was there before (assigned user or empty)
                      const previousValue = selectedContact?.website_assigned_to
                        ? `${getUserById(selectedContact.website_assigned_to)?.first_name} ${getUserById(selectedContact.website_assigned_to)?.last_name}`
                        : "";

                      setAssigneeSearch(prev => ({
                        ...prev,
                        [selectedContact!.contact_id]: previousValue,
                      }));

                      setShowAssignModal(false);
                      setSelectedAssignee(null);
                      setSelectedContact(null);
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    className="confirm-btn mailjet-confirm"
                    onClick={async () => {
                      await assignContact();
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Complete Contact Modal */}
      {showCompleteModal && contactToComplete && (
        <div className="add-item-popup-overlay">
          <div className="add-item-popup-content hh-del-pop">
            {isCompleting ? (
              <div
                style={{
                  height: "10rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LoadingSpinner />
              </div>
            ) : (
              <div className="hh-delete-inner-pop" style={{ gap: "2rem" }}>
                <div className="hh-delete-text-and-icon">

                  <div className="marketing-finish-icon-div">
                    <FaFlagCheckered className="marketing-finish-icon" />
                  </div>

                  <div>
                    <h3>Mark Contact as Complete</h3>

                    <p>
                      You are about to mark this contact request as complete:
                    </p>

                    <p style={{ marginTop: "1rem" }}>
                      <strong>Contact:</strong><br />
                      {contactToComplete.contact_first_name} {contactToComplete.contact_last_name}
                    </p>

                    <p style={{ marginTop: "0.5rem" }}>
                      <strong>Subject:</strong><br />
                      {contactToComplete.website_subject}
                    </p>

                    <p style={{ marginTop: "1rem", color: "#8A94A6", fontSize: "14px" }}>
                      This action will mark the contact as resolved and update the completion date.
                    </p>
                  </div>
                </div>

                <div className="hh-duo hh-actions">
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      setShowCompleteModal(false);
                      setContactToComplete(null);
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    className="confirm-btn mailjet-confirm"
                    onClick={async () => {
                      await markContactComplete();
                    }}
                  >
                    Mark as Complete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Complete Contact Modal */}
      {showConferencePopup && (
        <div className="active-products-popup-overlay">
          <div className="active-products-popup-content conferences-popup-content">

            <div className="conference-popup-header">
              <h3>Conferences 2026</h3>
              <button className="marketing-list-entry-button"
                onClick={() => {
                  setShowConferencePopup(false);
                }}>

                Close
              </button>
            </div>


            <div className="conference-grid">
              {Object.entries(groupedConferences).map(([industry, items]) => (
                <div key={industry} className="conferece-table">
                  <h3 style={{ color: mapNotionColor(items[0].industry_color) }}>
                    {industry}
                  </h3>

                  <div className="mailjet-contact-table-header conferece-header">
                    <div>Name</div>
                    <div>Date</div>
                    <div>Status</div>
                  </div>

                  <div className="conference-table-body">
                    {items.map((conf) => (
                      <div key={conf.id} className="conference-row">
                        <div className="conference-item">{conf.name}</div>
                        <div className="conference-item">{conf.date}</div>
                        <div className="conference-item">
                          <span
                            className="conference-status-pill"
                            style={{
                              backgroundColor: mapNotionStatusColor(conf.status_color).background,
                              color: "white"
                            }}
                          >
                            {conf.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>


          </div>
        </div>
      )}

    </div>
  );
};

export default MarketingMain;